import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, X } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetailPage = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/projects/${slug}`);
        setProject(response.data);
      } catch (error) {
        console.error('Error fetching project:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 animate-pulse">
            <div className="h-96 bg-slate-200 rounded-lg" />
            <div className="mt-8 h-10 bg-slate-200 rounded w-1/2" />
            <div className="mt-4 h-6 bg-slate-200 rounded w-3/4" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="pt-32 pb-16 text-center">
          <h1 className="text-3xl font-bold text-slate-900">Project not found</h1>
          <Link to="/portfolio" className="text-cyan-600 hover:underline mt-4 inline-block">
            Back to Portfolio
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Helper function to get image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    return url.startsWith('/api') ? `${BACKEND_URL}${url}` : url;
  };

  // Helper function to convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return '';
    
    // Already an embed URL
    if (url.includes('youtube.com/embed/')) {
      return url;
    }
    
    // Extract video ID from various YouTube URL formats
    let videoId = null;
    
    // Format: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }
    
    // Format: https://youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
      videoId = shortMatch[1];
    }
    
    // Format: https://www.youtube.com/v/VIDEO_ID
    const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
    if (vMatch) {
      videoId = vMatch[1];
    }
    
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Return original URL if we can't parse it
    return url;
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Image */}
      <section className="pt-24">
        <div className="relative h-[60vh] min-h-[400px]">
          <img
            src={getImageUrl(project.featured_image)}
            alt={project.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      </section>

      {/* Project Info */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <div className="flex flex-wrap gap-3 mb-4">
            {project.categories.map((category, idx) => (
              <span
                key={idx}
                className="px-4 py-1 bg-cyan-100 text-cyan-700 rounded-full text-sm font-medium"
              >
                {category}
              </span>
            ))}
          </div>

          {/* Title */}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-8">
            {project.name}
          </h1>

          {/* Description */}
          <p className="text-lg text-slate-600 leading-relaxed mb-12">
            {project.description}
          </p>

          {/* Project Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12 pb-12 border-b border-slate-200">
            {project.client_name && (
              <div>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Client</h4>
                <p className="text-slate-900 font-medium">{project.client_name}</p>
              </div>
            )}
            {project.location && (
              <div>
                <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Location</h4>
                <p className="text-slate-900 font-medium">{project.location}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-2">Status</h4>
              <p className="text-slate-900 font-medium">{project.status}</p>
            </div>
          </div>

          {/* Videos Section - Side by Side */}
          {(project.video_url?.trim() || project.testimonial_video_url?.trim()) && (
            <div className={`mb-12 grid gap-6 ${
              project.video_url?.trim() && project.testimonial_video_url?.trim() 
                ? 'grid-cols-1 lg:grid-cols-2' 
                : 'grid-cols-1'
            }`}>
              {/* Project Video */}
              {project.video_url && project.video_url.trim() !== '' && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Project Video</h2>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                    <iframe
                      src={getYouTubeEmbedUrl(project.video_url)}
                      title={project.video_title || 'Project Video'}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                  {project.video_title && (
                    <p className="mt-3 text-center text-slate-600 font-medium">{project.video_title}</p>
                  )}
                </div>
              )}

              {/* Testimonial Video */}
              {project.testimonial_video_url && project.testimonial_video_url.trim() !== '' && (
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Client Testimonial</h2>
                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-100">
                    <iframe
                      src={getYouTubeEmbedUrl(project.testimonial_video_url)}
                      title={project.testimonial_video_title || 'Client Testimonial'}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                  {project.testimonial_video_title && (
                    <p className="mt-3 text-center text-slate-600 font-medium">{project.testimonial_video_title}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Scope of Work */}
          {project.scope_of_work && project.scope_of_work.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Scope of Work</h2>
              <ul className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {project.scope_of_work.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-slate-600">
                    <span className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Testimonial */}
          {project.testimonial_quote && (
            <div className="mb-12 bg-slate-50 rounded-2xl p-8">
              <blockquote className="text-xl text-slate-700 italic leading-relaxed mb-4">
                "{project.testimonial_quote}"
              </blockquote>
              {project.testimonial_author && (
                <cite className="text-slate-900 font-semibold not-italic">
                  — {project.testimonial_author}
                </cite>
              )}
            </div>
          )}

          {/* Highlighted Scope */}
          {project.highlighted_scope && project.highlighted_scope.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Highlighted Scope</h2>
              <div className="grid gap-6">
                {project.highlighted_scope.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-cyan-500 pl-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Categorized Image Gallery */}
          {project.categorized_images && (
            Object.values(project.categorized_images).some(arr => arr && arr.length > 0) ? (
              <div className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6">Project Gallery</h2>
                
                {/* Completion Images - First */}
                {project.categorized_images.completion && project.categorized_images.completion.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-green-500">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-green-700">Completion</h3>
                        <p className="text-sm text-green-600">{project.categorized_images.completion.length} photos</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {project.categorized_images.completion.map((image, idx) => (
                        <div
                          key={`completion-${idx}`}
                          className="aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-green-200 shadow-sm hover:shadow-md transition-shadow"
                          onClick={() => setSelectedImage(getImageUrl(image))}
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`${project.name} - Completion ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-slate-100', 'flex', 'items-center', 'justify-center'); e.target.parentElement.innerHTML = `<span class="text-slate-400 text-xs p-2 text-center">Image unavailable</span>`; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Progress Images - Second */}
                {project.categorized_images.progress && project.categorized_images.progress.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-blue-500">
                      <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-blue-700">Progress</h3>
                        <p className="text-sm text-blue-600">{project.categorized_images.progress.length} photos</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {project.categorized_images.progress.map((image, idx) => (
                        <div
                          key={`progress-${idx}`}
                          className="aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-blue-200 shadow-sm hover:shadow-md transition-shadow"
                          onClick={() => setSelectedImage(getImageUrl(image))}
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`${project.name} - Progress ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-slate-100', 'flex', 'items-center', 'justify-center'); e.target.parentElement.innerHTML = `<span class="text-slate-400 text-xs p-2 text-center">Image unavailable</span>`; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Before Images - Last */}
                {project.categorized_images.before && project.categorized_images.before.length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center gap-3 mb-5 pb-3 border-b-2 border-amber-500">
                      <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-amber-700">Before</h3>
                        <p className="text-sm text-amber-600">{project.categorized_images.before.length} photos</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {project.categorized_images.before.map((image, idx) => (
                        <div
                          key={`before-${idx}`}
                          className="aspect-square cursor-pointer overflow-hidden rounded-lg border-2 border-amber-200 shadow-sm hover:shadow-md transition-shadow"
                          onClick={() => setSelectedImage(getImageUrl(image))}
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`${project.name} - Before ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.classList.add('bg-slate-100', 'flex', 'items-center', 'justify-center'); e.target.parentElement.innerHTML = `<span class="text-slate-400 text-xs p-2 text-center">Image unavailable</span>`; }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null
          )}

          {/* Legacy Image Gallery (for backwards compatibility) */}
          {project.images && project.images.length > 0 && !(project.categorized_images && Object.values(project.categorized_images).some(arr => arr && arr.length > 0)) && (
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Project Gallery</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {project.images.map((image, idx) => (
                  <div
                    key={idx}
                    className="aspect-square cursor-pointer overflow-hidden rounded-lg"
                    onClick={() => setSelectedImage(getImageUrl(image))}
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${project.name} - Image ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Back to Portfolio */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <Link
              to="/portfolio"
              className="inline-flex items-center text-cyan-600 hover:text-cyan-700 font-medium"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 w-12 h-12 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          <img
            src={selectedImage}
            alt="Full size"
            className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ProjectDetailPage;
